import { useCallback, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';

import { useStore } from 'core/store';

/**
 * Saves incident ignores on an offer through the offer page's own update (#36871): the new lists go
 * into the form, then the form submits, which re-rates the offer as "Update offer price" does.
 *
 * The submit waits for the render that carries the new values: Formik submits the values of the last
 * render, so submitting in the same tick as setFieldValue would send the old lists. Resolves true when
 * the re-rate produced a new offer (a rejection, a failed request or a form validation error don't). When
 * it didn't, the lists go back to what they were, so a later "Update offer price" can't save an ignore the
 * underwriter then discarded.
 */
export const useOfferIncidentIgnores = () => {
  const formik = useFormikContext();
  const { offer: offerStore } = useStore();
  const [pendingSubmit, setPendingSubmit] = useState(null);

  useEffect(() => {
    if (!pendingSubmit) {
      return;
    }
    setPendingSubmit(null);
    const offerIdBefore = offerStore.offer?.id;
    formik
      .submitForm()
      .then(() => pendingSubmit.resolve(!!offerStore.offer?.id && offerStore.offer.id !== offerIdBefore))
      .catch(() => pendingSubmit.resolve(false));
  }, [pendingSubmit, formik, offerStore]);

  return useCallback(
    ({ driverLists = {}, ignoredHomeIncidents }) =>
      new Promise((resolve) => {
        const changes = [];
        // a removed driver's list still applies to rows that moved onto the drivers who stay
        for (const [driverId, list] of Object.entries(driverLists)) {
          for (const field of ['drivers', 'removedDrivers']) {
            const index = (formik.values[field] || []).findIndex((person) => person?.id === driverId);
            if (index >= 0) {
              changes.push([`${field}.${index}.ignoredIncidents`, formik.values[field][index].ignoredIncidents, list]);
            }
          }
        }
        if (ignoredHomeIncidents) {
          changes.push(['ignoredHomeIncidents', formik.values.ignoredHomeIncidents, ignoredHomeIncidents]);
        }
        for (const [field, , value] of changes) {
          formik.setFieldValue(field, value, false);
        }
        setPendingSubmit({
          resolve: (saved) => {
            if (!saved) {
              for (const [field, previous] of changes) {
                formik.setFieldValue(field, previous, false);
              }
            }
            resolve(saved);
          }
        });
      }),
    [formik]
  );
};
